import {isAfter, startOfDay} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';
import {openDialog, useLocalStorage} from 'platform/components';

import {useEffect} from 'react';

import {isNotNil} from 'ramda';
import {isFalsy} from 'ramda-adjunct';

import {
  useGetMultiFactorAuthenticationSettingsQuery,
  useGetMyMultiFactorAuthenticationSettingsQuery,
} from '@dms/api/base';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {GRACE_PERIOD_DIALOG_ID} from '../constants/GRACE_PERIOD_DIALOG_ID';
import {GracePeriodNotification} from '../GracePeriodNotification';

const GRACE_PERIOD_NOTIFICATION_KEY = 'gracePeriodNotificationLastClosed';

export function useGracePeriodNotification() {
  const {data: userMfaSettings} = useGetMyMultiFactorAuthenticationSettingsQuery();
  const {data: mfaSettings} = useGetMultiFactorAuthenticationSettingsQuery();
  const [lastClosedDate, setLastClosedDate] = useLocalStorage<string | null>(
    GRACE_PERIOD_NOTIFICATION_KEY,
    null
  );

  const [canReadMfa] = usePermissions({
    permissionKeys: ['canReadUserMultiFactorAuthenticationSettings'],
  });
  const isMfaFeatureEnabled = isFeatureEnabled(featureFlags.SETTINGS_TWO_FACTOR_AUTH);

  useEffect(
    function showGracePeriodNotification() {
      const isUserDeactivated = isNotNil(userMfaSettings?.deactivatedAt);
      const shouldShowToday =
        !lastClosedDate || isAfter(startOfDay(new Date()), startOfDay(lastClosedDate));

      if (
        !userMfaSettings ||
        !mfaSettings ||
        !isMfaFeatureEnabled ||
        !canReadMfa ||
        isUserDeactivated ||
        !shouldShowToday
      ) {
        return;
      }

      // If MFA is already active, clean up and don't show notification
      if (isNotNil(userMfaSettings.activatedAt) || isFalsy(mfaSettings?.active)) {
        localStorage.removeItem(GRACE_PERIOD_NOTIFICATION_KEY);
        return;
      }

      openDialog(<GracePeriodNotification activateUntil={userMfaSettings.activateUntil} />, {
        title: i18n.t('page.myProfile.labels.multiFactorAuth'),
        onCloseComplete: () => setLastClosedDate(new Date().toISOString()),
        'data-testid': 'grace-period-notification-dialog',
        id: GRACE_PERIOD_DIALOG_ID,
        size: 'small',
      });
    },
    [userMfaSettings, lastClosedDate, setLastClosedDate, mfaSettings, isMfaFeatureEnabled] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );
}
